import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Pause, Pencil, Play, Plug, Plus, UserPlus } from "lucide-react";
import { useState } from "react";
import { useOrganization } from "@/api/queries";
import type { OrganizationDetail } from "@/api/types";
import { usePermissions } from "@/auth/permissions";
import { AiRunsTable } from "@/components/ai/ai-runs-table";
import { AuditTable } from "@/components/audit/audit-table";
import { ConversationsTable } from "@/components/conversation/conversations-table";
import { EmptyState } from "@/components/feedback/empty-state";
import { IdBadge } from "@/components/feedback/id-badge";
import { KeyValueList, Section } from "@/components/feedback/key-value";
import { ErrorPanel } from "@/components/feedback/query-state";
import { StatusBadge } from "@/components/feedback/status-badge";
import { type OneTimePassword, TemporaryPasswordDialog } from "@/components/feedback/temporary-password-dialog";
import { PageBody, PageHeader } from "@/components/layout/page-header";
import { IntegrationLink } from "@/components/navigation/entity-links";
import { OpportunitiesTable } from "@/components/opportunity/opportunities-table";
import { PurchasesTable } from "@/components/opportunity/purchases-table";
import { StoresTable } from "@/components/store/stores-table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { UsersTable } from "@/components/user/users-table";
import { StoreFormDialog } from "@/features/stores/store-form-dialog";
import { CreateUserDialog, DEALER_PASSWORD_NOTE } from "@/features/users/user-dialogs";
import { useLocalList } from "@/hooks/use-local-list";
import { formatDateTime, formatRelative } from "@/lib/dates";
import { formatNumber } from "@/lib/formatters";
import { INTEGRATION, ORGANIZATION } from "@/lib/status";
import { cn } from "@/lib/utils";
import { FollowupSettingsEditor } from "../ai/followup-settings-editor";
import { PromptSettingsEditor } from "../ai/prompt-settings-editor";
import { EditOrganizationDialog } from "./edit-organization-dialog";
import { OrgCrmEmail } from "./org-crm-email";
import { OrganizationStatusAction } from "./organization-status-action";

export const ORG_TABS = [
  "overview",
  "stores",
  "users",
  "opportunities",
  "conversations",
  "ai",
  "prompt",
  "followups",
  "purchases",
  "integrations",
  "audit",
  "settings",
] as const;
type OrgTab = (typeof ORG_TABS)[number];
const TAB_LABEL: Record<OrgTab, string> = {
  overview: "Overview",
  stores: "Stores",
  users: "Users",
  opportunities: "Opportunities",
  conversations: "Conversations",
  ai: "AI runs",
  prompt: "Rogue prompt",
  followups: "Follow-ups",
  purchases: "Purchases",
  integrations: "Integrations",
  audit: "Audit",
  settings: "Settings",
};

export function OrganizationDetailPage() {
  const { organizationId } = useParams({ from: "/_authenticated/organizations/$organizationId/" });
  const { tab = "overview" } = useSearch({ from: "/_authenticated/organizations/$organizationId/" });
  const q = useOrganization(organizationId);
  const { can } = usePermissions();
  const [editing, setEditing] = useState(false);
  const [statusAction, setStatusAction] = useState<"suspend" | "reactivate" | null>(null);
  const o = q.data;

  if (q.isError && !o)
    return (
      <PageBody>
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} resource="organization" />
      </PageBody>
    );

  return (
    <>
      <PageHeader
        crumbs={[{ label: "Organizations", to: "/organizations" }, { label: o?.name ?? "…" }]}
        title={
          o ? (
            <span className="flex items-center gap-2">
              {o.name} <StatusBadge config={ORGANIZATION[o.status]} />
            </span>
          ) : (
            <Skeleton className="h-5 w-56" />
          )
        }
        meta={
          o ? (
            <>
              <span>
                Organization ID <IdBadge id={o.id} full className="text-foreground" />
              </span>
              <span>Created {formatDateTime(o.createdAt)}</span>
              <span>Plan {o.plan ?? "—"}</span>
              <span>Last activity {formatRelative(o.lastActivityAt)}</span>
            </>
          ) : null
        }
        actions={
          o ? (
            <>
              {can("organizations:update") ? (
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                  <Pencil data-icon="inline-start" />
                  Edit
                </Button>
              ) : null}
              {can("organizations:suspend") ? (
                o.status === "SUSPENDED" ? (
                  <Button size="sm" variant="outline" onClick={() => setStatusAction("reactivate")}>
                    <Play data-icon="inline-start" />
                    Reactivate
                  </Button>
                ) : (
                  <Button size="sm" variant="destructive" onClick={() => setStatusAction("suspend")}>
                    <Pause data-icon="inline-start" />
                    Suspend
                  </Button>
                )
              ) : null}
            </>
          ) : null
        }
      >
        <nav aria-label="Organization sections" className="-mb-2.5 flex gap-0.5 overflow-x-auto">
          {ORG_TABS.map((t) => (
            <Link
              key={t}
              from="/organizations/$organizationId/"
              to="."
              search={{ tab: t === "overview" ? undefined : t }}
              aria-current={t === tab ? "page" : undefined}
              className={cn(
                "shrink-0 border-b-2 px-2.5 pb-2 text-xs",
                t === tab ? "border-foreground font-medium text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {TAB_LABEL[t]}
            </Link>
          ))}
        </nav>
      </PageHeader>
      <PageBody>{o ? <TabContent tab={tab} org={o} /> : <Skeleton className="h-64" />}</PageBody>
      {o && editing ? <EditOrganizationDialog org={o} open onOpenChange={setEditing} /> : null}
      {o && statusAction ? <OrganizationStatusAction org={o} action={statusAction} open onOpenChange={(v) => !v && setStatusAction(null)} /> : null}
    </>
  );
}

function TabContent({ tab, org }: { tab: OrgTab; org: OrganizationDetail }) {
  const f = { organizationId: org.id };
  const [stores, setStores] = useLocalList(f, { sort: "name", order: "asc" });
  const [users, setUsers] = useLocalList(f, { sort: "name", order: "asc" });
  const [opps, setOpps] = useLocalList(f, { sort: "updatedAt" });
  const [convs, setConvs] = useLocalList(f, { sort: "lastActivityAt" });
  const [runs, setRuns] = useLocalList(f, { sort: "createdAt" });
  const [purchases, setPurchases] = useLocalList(f, { sort: "purchasedAt" });
  const [audit, setAudit] = useLocalList(f, { sort: "at" });
  const [creating, setCreating] = useState<"store" | "user" | null>(null);
  const [credentials, setCredentials] = useState<OneTimePassword | null>(null);
  const { can } = usePermissions();
  const navigate = useNavigate();
  switch (tab) {
    case "stores":
      return (
        <>
          <StoresTable
            query={stores}
            onChange={setStores}
            hide={["organization"]}
            toolbar={
              can("stores:manage") ? (
                <Button size="sm" onClick={() => setCreating("store")}>
                  <Plus data-icon="inline-start" />
                  New store
                </Button>
              ) : null
            }
          />
          <StoreFormDialog
            open={creating === "store"}
            onOpenChange={(o) => setCreating(o ? "store" : null)}
            organization={org}
            onSaved={(s) => void navigate({ to: "/stores/$storeId", params: { storeId: s.id } })}
          />
        </>
      );
    case "users":
      return (
        <>
          <UsersTable
            query={users}
            onChange={setUsers}
            hide={["organization"]}
            toolbar={
              can("users:manage") ? (
                <Button size="sm" onClick={() => setCreating("user")}>
                  <UserPlus data-icon="inline-start" />
                  Add user
                </Button>
              ) : null
            }
          />
          {creating === "user" ? (
            <CreateUserDialog
              organization={org}
              open
              onOpenChange={(o) => setCreating(o ? "user" : null)}
              onCreated={(r) =>
                r.temporaryPassword ? setCredentials({ name: r.user.name, email: r.user.email, temporaryPassword: r.temporaryPassword }) : null
              }
            />
          ) : null}
          <TemporaryPasswordDialog credentials={credentials} title="User created" note={DEALER_PASSWORD_NOTE} onClose={() => setCredentials(null)} />
        </>
      );
    case "opportunities":
      return <OpportunitiesTable query={opps} onChange={setOpps} hide={["organization"]} />;
    case "conversations":
      return <ConversationsTable query={convs} onChange={setConvs} hide={["organization"]} />;
    case "ai":
      return <AiRunsTable query={runs} onChange={setRuns} hide={["organization"]} />;
    case "prompt":
      return <PromptSettingsEditor organizationId={org.id} />;
    case "followups":
      return <FollowupSettingsEditor organizationId={org.id} />;
    case "purchases":
      return (
        <PurchasesTable
          query={purchases}
          onChange={setPurchases}
          hide={["organization"]}
          onRowClick={(id) => void navigate({ to: "/purchases", search: { trace: id } })}
        />
      );
    case "audit":
      return (
        <AuditTable
          query={audit}
          onChange={setAudit}
          hide={["organization"]}
          onRowClick={(id) => void navigate({ to: "/audit", search: { event: id, organizationId: org.id } })}
        />
      );
    case "integrations":
      return (
        <div className="space-y-4">
          <OrgCrmEmail org={org} />
          <Section title="Integrations" className="[&>div]:p-0">
            {org.integrations.length === 0 ? (
              <EmptyState icon={Plug} title="No integrations connected" description="This organization hasn't connected any lead sources, CRM or DMS." />
            ) : (
              <table className="w-full text-xs">
                <thead className="text-[11px] text-muted-foreground uppercase">
                  <tr className="border-b">
                    <th className="px-3 py-1.5 text-left font-medium">Integration</th>
                    <th className="px-3 text-left font-medium">Status</th>
                    <th className="px-3 text-left font-medium">Last sync</th>
                  </tr>
                </thead>
                <tbody>
                  {org.integrations.map((i) => (
                    <tr key={i.id} className="border-b last:border-b-0">
                      <td className="px-3 py-1.5">
                        <IntegrationLink id={i.id} name={i.name} />
                      </td>
                      <td className="px-3">
                        <StatusBadge config={INTEGRATION[i.status]} />
                      </td>
                      <td className="px-3 text-muted-foreground">{formatRelative(i.lastSyncAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      );
    case "settings":
      return (
        <Section title="Settings (read-only)">
          <KeyValueList
            items={Object.entries(org.settings).map(([k, v]) => [
              k,
              <span key={k} className="font-mono text-xs">
                {v === null ? "—" : String(v)}
              </span>,
            ])}
          />
          <p className="mt-2 text-[11px] text-muted-foreground">
            Editable fields (name, plan, Offer API) are under Edit. Other settings need backend contract support.
          </p>
        </Section>
      );
    default:
      return (
        <div className="grid gap-4 lg:grid-cols-2">
          <Section title="Summary">
            <KeyValueList
              items={[
                ["Stores", formatNumber(org.storeCount)],
                ["Users", formatNumber(org.userCount)],
                ["Active opportunities", formatNumber(org.activeOpportunities)],
                ["Conversations", formatNumber(org.counts.conversations)],
                ["Purchases (30d)", formatNumber(org.counts.purchases30d)],
              ]}
            />
          </Section>
          <Section title="Operations (24h)">
            <KeyValueList
              items={[
                ["AI runs", formatNumber(org.counts.aiRuns24h)],
                [
                  "AI failures",
                  <span key="f" className={org.counts.aiFailures24h ? "font-medium text-danger" : undefined}>
                    {formatNumber(org.counts.aiFailures24h)}
                  </span>,
                ],
                ["Integrations", formatNumber(org.counts.integrations)],
                ["Status", <StatusBadge key="s" config={ORGANIZATION[org.status]} />],
                ["Suspended", org.suspendedAt ? `${formatDateTime(org.suspendedAt)} — ${org.suspensionReason ?? "no reason"}` : "—"],
              ]}
            />
          </Section>
        </div>
      );
  }
}
